import { useEffect, useState } from 'react'

const CURRENT = typeof __BUILD_VERSION__ === 'string' ? __BUILD_VERSION__ : ''

export interface VersionCheckState {
  hasUpdate: boolean
  currentVersion: string | null
  latestVersion: string | null
}

// Lightweight "new version available" detector. Fetches /version.json on
// mount and on every window focus, compares to the version baked into the
// running bundle, and flips a flag. Network cost: ~50 bytes per focus
// event (no polling), so it's cheap enough to live everywhere.
export function useVersionCheck(): VersionCheckState {
  const [latestVersion, setLatestVersion] = useState<string | null>(null)

  useEffect(() => {
    if (!CURRENT) return // dev mode without injected version

    let cancelled = false

    const check = async () => {
      if (cancelled) return
      try {
        const res = await fetch('/version.json', { cache: 'no-store' })
        if (!res.ok) return
        const data = (await res.json()) as { version?: string }
        if (data.version && data.version !== CURRENT) {
          setLatestVersion(data.version)
        }
      } catch {
        // Network blip — silent. Will retry on next focus.
      }
    }

    check()
    window.addEventListener('focus', check)
    return () => {
      cancelled = true
      window.removeEventListener('focus', check)
    }
  }, [])

  return {
    hasUpdate: latestVersion !== null,
    currentVersion: CURRENT || null,
    latestVersion,
  }
}
